// /penrose: the Penrose community-bot landing. Screenshot-free but rich — hero,
// stats band, featured + grid features, a command-category overview (full list
// lives at /penrose/commands), a short FAQ, and a closing CTA.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, ArrowRight, ShieldCheck, Wand2, Trophy, Coins,
  UserPlus, Bell, Gift, Sparkles, Radio, ChevronRight, Plus,
} from 'lucide-react';
import { PENROSE_COMMANDS } from '../data/penroseCommands';

const SUPPORT_URL = 'https://discord.gg/2xvuF9TES7';
const DASHBOARD_URL = 'https://penrose.streamnook.app';
// Administrator invite (permissions=8) so new servers get full perms — no ghost-room issues.
const INVITE_URL =
  'https://discord.com/oauth2/authorize?client_id=1436402207485464596&permissions=8&scope=bot+applications.commands';

const TOTAL = PENROSE_COMMANDS.reduce((n, c) => n + c.commands.length, 0);

const FAQ: { q: string; a: string }[] = [
  { q: 'What is Penrose?', a: 'An all-in-one Discord bot for running a community — moderation, safety, engagement, and automation in one place, configured from a web dashboard.' },
  { q: 'Is it hard to set up?', a: 'No. Add it, open the dashboard, and toggle what you want. Most features work out of the box with sensible defaults.' },
  { q: 'Does it need Administrator?', a: 'For full protection (anti-nuke, role management, moderation) Penrose runs best with Administrator. You can run a limited set with fewer permissions.' },
  { q: 'Can I manage it from the web?', a: 'Yes — every module has a dashboard page, so you rarely need to touch a command to configure anything.' },
];

export default function Penrose() {
  return (
    <div className="space-y-24 pb-8">
      {/* Hero */}
      <section className="relative pt-6 text-center lg:pt-12">
        <div className="hero-beams" aria-hidden="true" />
        <div className="hero-aurora" aria-hidden="true" />
        <img src="/assets/penrose-logo.png" alt="Penrose logo" className="mx-auto h-24 w-24 object-contain" />
        <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-[12px] font-medium text-textSecondary">
          <Sparkles className="h-3.5 w-3.5 text-accent" />
          The all-in-one community bot
        </span>
        <h1 className="font-serif mx-auto mt-5 max-w-3xl text-6xl font-normal leading-[0.98] tracking-[-0.01em] text-white sm:text-7xl">
          Everything your server needs, in one bot.
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-textSecondary leading-relaxed">
          Penrose handles moderation, safety, engagement, and automation — so your community runs
          itself and you get to enjoy it. Configure it all from a clean web dashboard.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a href={INVITE_URL} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold">
            <Plus className="h-4 w-4" />
            Add to Discord
          </a>
          <a href={DASHBOARD_URL} target="_blank" rel="noreferrer" className="glass-button inline-flex items-center gap-2 px-5 py-3 text-sm font-medium">
            <LayoutDashboard className="h-4 w-4" />
            Open dashboard
          </a>
        </div>
      </section>

      {/* Stats band */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          [`${TOTAL}`, 'Commands'],
          [`${PENROSE_COMMANDS.length}`, 'Categories'],
          ['24/7', 'Always online'],
          ['1', 'Dashboard for it all'],
        ].map(([v, k]) => (
          <div key={k} className="settings-card px-5 py-6 text-center">
            <div className="text-3xl font-bold text-textPrimary">{v}</div>
            <div className="mt-1 text-[11px] font-semibold uppercase tracking-widest text-textMuted">{k}</div>
          </div>
        ))}
      </section>

      {/* Featured features */}
      <section className="space-y-4">
        <SectionHeading eyebrow="Built for real communities" title="Powerful where it counts." />
        <div className="grid gap-4 md:grid-cols-2">
          <FeatureBig
            icon={<ShieldCheck className="h-6 w-6" />}
            title="Safety that reacts in real time"
            body="A real-time anti-nuke engine stops mass-deletes and rogue admins instantly, backed by a full moderation suite and automod for spam, links, and invites."
            points={['Instant anti-nuke lockdown', 'Bans, mutes, warnings & mod logs', 'Automod filters']}
          />
          <FeatureBig
            icon={<Wand2 className="h-6 w-6" />}
            title="Automation that runs itself"
            body="Onboarding, support, and alerts happen without you. Set it up once from the dashboard and let Penrose handle the day-to-day."
            points={['Welcome flows, autorole & Join-to-Create', 'Tickets & modmail with transcripts', 'Twitch & YouTube alerts']}
          />
        </div>
      </section>

      {/* Feature grid */}
      <section>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <MiniFeature icon={<Trophy className="h-5 w-5" />} title="Leveling" body="Text & voice XP, rank cards, and leaderboards that reward your most active members." />
          <MiniFeature icon={<Coins className="h-5 w-5" />} title="Economy" body="A full server currency with shops, rewards, and role unlocks." />
          <MiniFeature icon={<Gift className="h-5 w-5" />} title="Giveaways" body="Run fair, timed giveaways with entry requirements and automatic winners." />
          <MiniFeature icon={<UserPlus className="h-5 w-5" />} title="Roles & menus" body="Autorole, reaction roles, and dropdown role menus for hands-free onboarding." />
          <MiniFeature icon={<Radio className="h-5 w-5" />} title="Social alerts" body="Announce Twitch go-lives and YouTube uploads the moment they happen." />
          <MiniFeature icon={<Bell className="h-5 w-5" />} title="AI assistant" body="A built-in assistant your members can ask for help, answers, and more." />
        </div>
      </section>

      {/* Command categories overview */}
      <section>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <SectionHeading eyebrow="Commands" title={`${TOTAL} commands, neatly organized.`} />
          <Link to="/penrose/commands" className="glass-button inline-flex items-center gap-2 self-start px-4 py-2 text-sm font-medium">
            Browse all commands <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {[...PENROSE_COMMANDS]
            .sort((a, b) => b.commands.length - a.commands.length)
            .map((c) => (
              <Link
                key={c.category}
                to="/penrose/commands"
                className="settings-card group flex items-center justify-between px-4 py-3 transition-colors hover:border-[var(--color-border)]"
              >
                <span className="text-sm font-medium text-textPrimary">{c.category}</span>
                <span className="inline-flex items-center gap-1 text-xs text-textMuted">
                  {c.commands.length}
                  <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
        </div>
      </section>

      {/* FAQ */}
      <section>
        <SectionHeading eyebrow="Questions" title="Good to know." />
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {FAQ.map((f) => (
            <article key={f.q} className="settings-card p-5">
              <h3 className="font-semibold text-textPrimary">{f.q}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-textSecondary">{f.a}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="settings-card p-10 text-center sm:p-14">
        <h2 className="text-3xl font-semibold tracking-tight">Add Penrose to your server</h2>
        <p className="mx-auto mt-3 max-w-xl text-textSecondary">
          Set everything up from the web dashboard, or hop into the support server if you need a hand.
        </p>
        <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a href={INVITE_URL} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold">
            <Plus className="h-4 w-4" />
            Add to Discord
          </a>
          <a href={DASHBOARD_URL} target="_blank" rel="noreferrer" className="glass-button inline-flex items-center gap-2 px-5 py-3 text-sm font-medium">
            <LayoutDashboard className="h-4 w-4" />
            Open dashboard
          </a>
          <a href={SUPPORT_URL} target="_blank" rel="noreferrer" className="glass-button inline-flex items-center gap-2 px-5 py-3 text-sm font-medium">
            Support <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
}

function SectionHeading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-widest text-accent">{eyebrow}</p>
      <h2 className="mt-2 text-3xl font-semibold tracking-tight">{title}</h2>
    </div>
  );
}

function FeatureBig({ icon, title, body, points }: { icon: ReactNode; title: string; body: string; points: string[] }) {
  return (
    <article className="settings-card flex flex-col p-6">
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[var(--color-borderLight)] text-accent" style={{ background: 'var(--color-accent-dim)' }}>
        {icon}
      </div>
      <h3 className="mt-4 text-lg font-semibold text-textPrimary">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-textSecondary">{body}</p>
      <ul className="mt-4 space-y-1.5">
        {points.map((p) => (
          <li key={p} className="flex items-center gap-2 text-[13px] text-textSecondary">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            {p}
          </li>
        ))}
      </ul>
    </article>
  );
}

function MiniFeature({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <article className="settings-card p-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--color-borderLight)] text-accent" style={{ background: 'var(--color-accent-dim)' }}>
        {icon}
      </div>
      <h3 className="mt-3 font-semibold text-textPrimary">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-textSecondary">{body}</p>
    </article>
  );
}
